import React from 'react'

import { COMMON_ERROR } from 'utils/ErrorMessages'
import { getCombinedId } from 'utils/helpers'
import { getActiveIntegrationLabel } from 'utils/integrations'
import { getPendoVisitorPreferences } from 'utils/pendo'
import { getPlanName } from 'utils/plans'
import { groupPosthogCompany, identifyPosthogUser, posthogAppProperties } from 'utils/posthog'
import { getRoleNameById, OWNER_ROLE } from 'utils/roles'
import { initThirdPartyScripts } from 'utils/thirdPartyScripts'
import { URL_CONST } from 'utils/urlConsts'

import { DeepfakeNotificationsManager } from 'components/pages/deepfake/notifications'

import * as Sentry from '@sentry/browser'

/**
 * Any `a.support-link` anywhere in the app opens the Zendesk widget. This was a
 * jQuery handler inlined in the Django shell; it lives here so the static
 * Cloudflare shell behaves the same and so it can be gated on props rather than
 * on a Django template condition. Module scope keeps the reference stable for
 * add/removeEventListener.
 */
const supportLinkClickHandler = (e) => {
  const supportLink = e.target?.closest?.('.support-link')

  if (!supportLink) return

  const launcher = document
    .getElementById('launcher')
    ?.contentDocument?.querySelector('.src-component-Launcher-wrapper')

  if (!launcher) return

  e.preventDefault()
  launcher.click()
}

export default class App extends React.Component {
  state = {
    hasError: false,
    // initThirdPartyScripts resolves when third-party startup is done.
    canPaint: false,
  }

  bindSupportLinkLauncher = ({ isMaster, showSupportButton }) => {
    if (isMaster || !showSupportButton || this.isSupportLinkLauncherBound) return

    document.addEventListener('click', supportLinkClickHandler)
    this.isSupportLinkLauncherBound = true
  }

  componentDidMount() {
    const { isDataLoaded } = this.props

    // StrictMode simulates unmount → remount on the SAME instance, so the flag
    // componentWillUnmount set would otherwise stick and the canPaint update
    // below would be dropped, leaving the shell preloader up forever.
    this.isUnmounted = false

    if (isDataLoaded) {
      this.initializeServices(this.props)
    } else {
      this.props.fetchAppData()
    }

    if (window.innerWidth <= 750 && window.location.pathname.indexOf('attackdetails') === -1) {
      this.props.history.push(URL_CONST.incidents.self)
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isDataLoaded && this.props.isDataLoaded) {
      this.initializeServices(this.props)
    }
  }

  initializeServices = (relevantProps) => {
    this.bindSupportLinkLauncher(relevantProps)

    this.props.incidentsCountPolling()

    // Only start deepfake polling if the feature flag is enabled
    if (relevantProps.deepfakeVisibility) {
      this.props.deepfakeNotificationsPolling()
    }

    const combinedUserId = getCombinedId(
      relevantProps.environment,
      relevantProps.region,
      relevantProps.user.id
    )

    const combinedCompanyId = getCombinedId(
      relevantProps.environment,
      relevantProps.region,
      relevantProps.selfCompanyId
    )

    // Must run before the dataLayer push and pendo.initialize below: loading GTM
    // is what creates window.dataLayer, and loading Pendo is what creates
    // window.pendo, now that the shells no longer carry the inline snippets.
    initThirdPartyScripts({
      isOnPremise: relevantProps.environment === 'ONPREMISE',
      // Mirrors the `is_saas` template flag — settings.PRODUCTION, i.e. DEPLOYMENT == 'PROD'.
      isSaas: relevantProps.environment === 'PROD',
      allowBablic: relevantProps.allowBablic,
      deployment: relevantProps.environment,
      region: relevantProps.region,
    }).then(() => {
      if (this.isUnmounted) return

      this.setState({ canPaint: true })
    })

    // A StrictMode remount replays initializeServices on the same instance; don't re-identify.
    if (this.analyticsIdentified) return

    this.analyticsIdentified = true

    // Google Tag Manager
    // GTM dataLayer https://developers.google.com/tag-manager/devguide
    if (typeof window.dataLayer?.push === 'function') {
      window.dataLayer.push({
        userId: combinedUserId,
        companyId: combinedCompanyId,
      })
    }

    const roleName = relevantProps.isOwner
      ? OWNER_ROLE.name
      : getRoleNameById(relevantProps.user.role)

    identifyPosthogUser(
      combinedUserId,
      {
        email: relevantProps.user.email,
        name: relevantProps.user.fullName,
        full_name: relevantProps.user.fullName,
        role: roleName,
        internal: !!relevantProps.isMaster,
        is_partner: relevantProps.isPartner,
        static_demo: relevantProps.isStaticSalesDemo,
      },
      {
        surface: 'spa',
        ...posthogAppProperties({
          deployment: relevantProps.environment,
          region: relevantProps.region,
        }),
        partner_company_id: combinedCompanyId,
        partner_company_name: relevantProps.selfCompanyName,
        is_partner: relevantProps.isPartner,
        user_role: roleName,
        app_version: GIT_VERSION,
      }
    )

    groupPosthogCompany(combinedCompanyId, {
      name: relevantProps.selfCompanyName,
      company_name: relevantProps.selfCompanyName,
      integration: getActiveIntegrationLabel(relevantProps.selfIntegrations),
      plan: getPlanName(relevantProps.selfLicenses?.plan),
      is_partner: relevantProps.isPartner,
      brand_name: relevantProps.brandName,
      partner_name: relevantProps.partnerName,
      mailbox_limit: relevantProps.mailboxLimit,
      mailbox_enabled: relevantProps.mailboxEnabled,
      static_demo: relevantProps.isStaticSalesDemo,
      trigger_based_campaigns_enabled: relevantProps.isTriggerBasedCampaignsEnabled,
    })

    // window.pendo here is the stub initThirdPartyScripts just installed: it queues
    // this call until the agent script arrives, so initializing right away is safe.
    // The guard is only false where Pendo is never loaded (on-premise, unit tests).
    // To cover the MSP case use pendoMSPSupportMiddleware.
    if (typeof window.pendo?.initialize === 'function') {
      window.IRONSCALES.pendoData = {
        visitor: {
          id: combinedUserId,
          email: relevantProps.user.email,
          full_name: relevantProps.user.fullName,
          role: roleName,
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'None',
          internal: !!relevantProps.isMaster,
          is_partner: relevantProps.isPartner,
          static_demo: relevantProps.isStaticSalesDemo,
          ...getPendoVisitorPreferences(),
        },

        account: {
          id: combinedCompanyId,
          company_name: relevantProps.selfCompanyName,
          integration: getActiveIntegrationLabel(relevantProps.selfIntegrations),
          plan: getPlanName(relevantProps.selfLicenses?.plan),
          is_partner: relevantProps.isPartner,
          brand_name: relevantProps.brandName,
          partner_name: relevantProps.partnerName,
          mailbox_limit: relevantProps.mailboxLimit,
          mailbox_enabled: relevantProps.mailboxEnabled,
          static_demo: relevantProps.isStaticSalesDemo,
          trigger_based_campaigns_enabled: relevantProps.isTriggerBasedCampaignsEnabled,
        },
      }

      window.pendo.initialize(window.IRONSCALES.pendoData)
    } else if (relevantProps.environment !== 'ONPREMISE') {
      // Everywhere but on-premise the stub should exist by now; reaching here
      // means Pendo never loaded and analytics are silently off — say so.
      console.error('Pendo is not loaded — pendo.initialize was skipped')
    }
  }

  Logo = () => (
    <img
      onLoad={(e) => e.target.classList.add('loaded')}
      src={`${STATIC_BASE_URL}/static/webapp/img/main-logo.svg`}
    />
  )

  componentDidCatch(error, info) {
    this.setState({ hasError: true })
    if (window.SENTRY_INITIALIZED && NODE_ENV !== 'development') {
      // eslint-disable-line
      Sentry.captureException(error, {
        user: {
          email: this.props.user.email,
        },
        extra: info,
        tags: {
          source: 'App.componentDidCatch',

          companyId: this.props.selfCompanyId,
        },
      })
    } else {
      console.error('App.componentDidCatch', error, info)
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true

    if (!this.isSupportLinkLauncherBound) return

    document.removeEventListener('click', supportLinkClickHandler)
    this.isSupportLinkLauncherBound = false
  }

  render() {
    const { error, isDataLoaded } = this.props

    if (error || this.state.hasError) {
      return (
        <div className="hide-preloader app-preloader">
          <div className="app-preloader-center-outer">
            <div className="app-preloader-center-middle">
              <this.Logo />
              <div className="app-preloader-error">{error?.detail || COMMON_ERROR}</div>
            </div>
          </div>
        </div>
      )
    }

    // Returning '' leaves the shell preloader up, which is where both shells already
    // sit while app data loads.
    if (isDataLoaded && this.state.canPaint) {
      return (
        <div className="hide-preloader-animated app-container">
          {this.props.children}
          <DeepfakeNotificationsManager />
        </div>
      )
    }

    return ''
  }
}
